import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { isEmpty } from 'lodash';
import { Image } from '@thd-olt-component-react/core-ui';
import {
  AccessTimeTwoTone, Business, DateRange, Link, LocationOn, Error
} from '@material-ui/icons';
import { Alert } from '@one-thd/sui-atomic-components';
import { Calendar } from '@one-thd/sui-icons';
import { JobPostingContext } from '../../../contexts/JobPostingContext';
import { formatDateMMDDYYYY, formatToMDY2Digits } from '../../../utils/formatters';
import { ActionsButtons } from './ActionsButtons/ActionsButtons';
import { EditableJobPostingField } from '../EditableJobPostingField/EditableJobPostingField';
import { EditableJobPostingPayRange } from '../EditableJobPostingField/EditableJobPostingPayRange';
import { useStore } from '../../../store';
import OverflowTooltip from '../../common/OverflowTooltip/OverflowTooltip';
import { generateImageSource } from '../../../utils/generateImageSource';
import { tradeTypeIconMap } from '../../../utils/constants';
import SkillBar from '../../ProfileSearch/ProfileDetails/SkillBar/SkillBar';
import { getExpiryLabel, getDaysToExpire } from '../../../utils/daysToExpire';
import CreateJobPostingDatePicker from '../CreateJobPostingContent/CreateJobPostingDatePicker';

const _JobPostingSideBar = () => {
  const { activeJobPosting, shouldRenderJobPostingField, editorModeActive } = useContext(JobPostingContext);

  function formatCurrency(value) {
    if (!Number.isNaN(value)) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    }
    return '';
  }

  const {
    staticDataStore: { tradeTypes, employmentTypes, yearsOfExperiences, payFrequencies }
  } = useStore();

  const [minMaxError, setMinMaxError] = useState(false);
  useEffect(() => {

    if (activeJobPosting?.payRange?.minAmount && activeJobPosting?.payRange?.maxAmount) {
      setMinMaxError(activeJobPosting?.payRange?.minAmount >= activeJobPosting?.payRange?.maxAmount);
    } else {
      setMinMaxError(false);
    }
  }, [activeJobPosting?.payRange?.minAmount, activeJobPosting?.payRange?.maxAmount]);

  const expiryLabel = getExpiryLabel(activeJobPosting?.deadlineToApply);
  const daysToExpire = getDaysToExpire(activeJobPosting?.deadlineToApply);

  const renderPostedDate = () => {

    return (
      <>
        {activeJobPosting.status !== 'DRAFT' && (
          <>
            <div className="ptp-job-posting__border-spacer" />
            <div
              className="ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-padding-wrapper"
              data-testid="job-posting-static-date"
            >
              <span className="ptp-job-posting__side-bar-mui-icon-spacer">
                <DateRange />
              </span>
                POSTED: {formatDateMMDDYYYY(activeJobPosting.postedDate)}
            </div>
          </>

        )}
      </>
    );
  };

  return (
    <>
      <div className="ptp-common__section ptp-job-posting__side-bar ptp-hidden__sm-up" style={{ marginBottom: '20px' }}>
        <div className="ptp-job-posting__side-bar-padding-wrapper ">

          {shouldRenderJobPostingField('title') && (
            <EditableJobPostingField
              className="ptp-common__heading-brand"
              jpKey="title"
              label="Job Title"
              required
              type="text"
              maxLength={50}
              errorText="Please provide a title"
            >
              <OverflowTooltip
                className="ptp-job-posting__side-bar-summary-section"
                byHeight
              >
                <p data-testid="job-posting-static-title">{activeJobPosting.title}</p>
              </OverflowTooltip>
            </EditableJobPostingField>
          )}
        </div>
      </div>

      <div className="ptp-common__section ptp-job-posting__side-bar">
        <div className="ptp-job-posting__side-bar-padding-wrapper">
          {
          /** Trade Type */
            shouldRenderJobPostingField('tradeType') && (
              <EditableJobPostingField
                className="ptp-job-posting__side-bar-spacer"
                jpKey="tradeType"
                label="Trade Type"
                required
                type="dropdown"
                options={tradeTypes}
                errorText="Please select a trade type"
              >
                <p
                  data-testid="job-posting-static-tradeType"
                  className="ptp-job-posting__side-bar-summary-section"
                >
                  <Image
                    src={generateImageSource(`/images/v1/path-to-pro/tradeTypes/${tradeTypeIconMap?.[activeJobPosting.tradeType] || 'residentialRemodeling'}.svg`)}
                    className="ptp-job-posting__side-bar-tradeType-icon"
                    alt="trade icon"
                    data-testid="tradeTypeIcon"
                  />
                  {activeJobPosting.tradeType}
                </p>
              </EditableJobPostingField>
            )
          }
          {
            shouldRenderJobPostingField('employmentType') && (
              <EditableJobPostingField
                className="ptp-job-posting__side-bar-spacer"
                jpKey="employmentType"
                label="Type of Employment"
                required
                type="dropdown"
                options={employmentTypes}
                errorText="Please select an employment type"
              >
                <p
                  data-testid="job-posting-static-employmentType"
                  className="ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-spacer"
                >
                  <span className="ptp-job-posting__side-bar-mui-icon-spacer">
                    <AccessTimeTwoTone />
                  </span>
                  {activeJobPosting.employmentType}
                </p>
              </EditableJobPostingField>
            )
          }

          {
            shouldRenderJobPostingField('locationText') && (
              <EditableJobPostingField
                className="ptp-job-posting__side-bar-spacer"
                jpKey="locationText"
                label="Location"
                required
                type="location"
              >
                <p
                  data-testid="job-posting-static-locationText"
                  className="ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-spacer"
                >
                  <span className="ptp-job-posting__side-bar-mui-icon-spacer">
                    <LocationOn />
                  </span>
                  {activeJobPosting.locationText}
                </p>
              </EditableJobPostingField>
            )
          }
          {
          /** years of experience */
            shouldRenderJobPostingField('yearsOfExperience') && (
              <EditableJobPostingField
                jpKey="yearsOfExperience"
                label="Experience Level"
                required
                type="dropdown"
                errorText="Please select experience level"
                options={yearsOfExperiences}
              >
                <div
                  data-testid="job-posting-static-yearsOfExperience"
                  className="p2p-profile__experience-lvl-2 ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-spacer"
                >
                  <SkillBar yearsOfExperience={activeJobPosting.yearsOfExperience} altStyle />
                  <p>{activeJobPosting.yearsOfExperience}</p>
                </div>
              </EditableJobPostingField>
            )
          }
          {editorModeActive ? (
            <>
              {
                /** Pay Frequency */
                shouldRenderJobPostingField('payFrequency') && (
                  <EditableJobPostingField
                    jpKey="payFrequency"
                    label="Pay Frequency"
                    required
                    type="dropdown"
                    errorText="Please select the pay frequency"
                    options={payFrequencies}
                  >
                    <div
                      data-testid="job-posting-static-payFrequency"
                      className="p2p-profile__experience-lvl-2 ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-spacer"
                    >
                      <p>{activeJobPosting?.payFrequency}</p>
                    </div>
                  </EditableJobPostingField>
                )
              }
              {
                /** Min Amount */
                shouldRenderJobPostingField('payRange') && (
                  <EditableJobPostingPayRange
                    jpKey="minAmount"
                    label="Minimmum Pay"
                    type="text"
                    value={activeJobPosting?.payRange?.minAmount}
                    errorText="Please provide a number"
                    maxLength={50}
                    required
                  />
                )
              }
              {
                /** Max Amount */
                shouldRenderJobPostingField('payRange') && (
                  <EditableJobPostingPayRange
                    jpKey="maxAmount"
                    type="text"
                    value={activeJobPosting?.payRange?.maxAmount}
                    errorText="Please provide a number"
                    maxLength={50}
                    label="Maximum Pay"
                  />
                )
              }
              {minMaxError && (<Alert status="error">Please make the maximum more than the minimum</Alert>)}
            </>
          ) : (
            <div className="ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-spacer ptp-job-posting__pay-range">
              {formatCurrency(activeJobPosting?.payRange?.minAmount)}
              {activeJobPosting?.payRange?.maxAmount && ` - ${formatCurrency(activeJobPosting?.payRange?.maxAmount)}`}
              {' '}{activeJobPosting?.payFrequency}
            </div>
          )}
          {
            /** company name */
            shouldRenderJobPostingField('companyName') && (
              <EditableJobPostingField
                className="ptp-common__hyperlink ptp-job-posting__side-bar-spacer"
                jpKey="companyName"
                label="Company Name"
                required
                maxLength={50}
                errorText="Please provide a company name"
                type="text"
              >

                <div className="ptp-job-posting__border-spacer ptp-job-posting__side-bar-spacer" style={{ marginLeft: '-10px', marginRight: '-10px' }} />
                <p
                  data-testid="job-posting-static-company-name"
                  className="ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-spacer"
                  style={{ textDecoration: 'underline' }}
                >
                  <span className="ptp-job-posting__side-bar-mui-icon-spacer">
                    <Business />
                  </span>
                  {activeJobPosting.companyName}
                </p>

              </EditableJobPostingField>
            )
          }
          {
          /** company Url */
            shouldRenderJobPostingField('companyUrl') && (
              <EditableJobPostingField
                className="ptp-common__hyperlink ptp-job-posting__side-bar-spacer"
                jpKey="companyUrl"
                label="Company Url"
                maxLength={3000}
                errorText="Please provide a valid url"
                type="text"
              >

                <p
                  style={{ display: 'flex' }}
                  className="ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-spacer"
                >
                  <span className="ptp-job-posting__side-bar-mui-icon-spacer">
                    {/* eslint-disable-next-line */}
                    <Link />
                  </span>
                  <OverflowTooltip
                    className="ptp-common__hyperlink"
                    data-testid="job-posting-static-companyUrl"
                  >
                    <a href={addhttp(activeJobPosting.companyUrl)} target="_blank" rel="noreferrer">
                      {activeJobPosting.companyUrl}
                    </a>
                  </OverflowTooltip>
                </p>

              </EditableJobPostingField>
            )
          }

          {editorModeActive
            ? (
              <>
                <CreateJobPostingDatePicker showErrors isCreateJobPosting={false} />
                {expiryLabel?.text && (
                  <div className="ptp-job-posting__side-bar-expire-soon">
                    <Error className="ptp-job-posting__side-bar-expire-soon-icon" />
                    {expiryLabel.text}
                  </div>
                )}
              </>
            )
            : expiryLabel?.text && (
            <>
              <p className="ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-spacer ptp-job-posting__side-bar-expire-date">
                <Calendar size="small" />
                <span className="ptp-job-posting__side-bar-expire-date-text">Expires on {expiryLabel?.date ? formatToMDY2Digits(expiryLabel.date) : ''}
                </span>
              </p>
              <div className="ptp-job-posting__side-bar-expire-soon">
                <Error className="ptp-job-posting__side-bar-expire-soon-icon" />
                {expiryLabel.text}
              </div>
            </>
          )}
          {!editorModeActive && daysToExpire < 0 && (
            <p className="ptp-job-posting__side-bar-summary-section ptp-job-posting__side-bar-spacer ptp-job-posting__side-bar-expire-date">
              <Calendar size="small" />
              <span className="ptp-job-posting__side-bar-expire-date-text">Expired on {formatToMDY2Digits(activeJobPosting?.deadlineToApply)}
              </span>
            </p>
          )}
        </div>

        {!isEmpty(activeJobPosting) && <ActionsButtons status={activeJobPosting.status} />}
        {renderPostedDate()}
      </div>
    </>
  );

  function addhttp(url) {
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    return url;
  }
};

export const JobPostingSideBar = observer(_JobPostingSideBar);
